import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage, findPageKeyAndParams, getLocalizedPath } from './LanguageContext';
import { combineSchemas } from '../lib/schema';

const SEO = ({
  title,
  description,
  ogImage = 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/ceef4750-2ca2-43c1-b82b-e06ba062b589.jpeg', // Standard luksus hotelbillede fra Mallorca
  ogType = 'website',
  schemaMarkup = null,
  canonical = null
}) => {
  const location = useLocation();
  const { language, t } = useLanguage();
  const siteUrl = 'https://alorarejser.dk';
  
  const canonicalUrl = canonical
    ? (canonical.startsWith('http') ? canonical : `${siteUrl}${canonical.startsWith('/') ? '' : '/'}${canonical}`)
    : `${siteUrl}${location.pathname}`;
  
  const defaultTitle = t('seo_default_title');
  const fullTitle = title 
    ? (title.includes('Alora Rejser') || title.includes('|') ? title : `${title} | Alora Rejser`) 
    : defaultTitle;
  
  const defaultDescription = t('seo_default_desc');
  const metaDescription = description || defaultDescription;

  // Generate hreflang links
  const pageInfo = findPageKeyAndParams(location.pathname);
  const hreflangs = [];
  
  if (pageInfo) {
    const daPath = getLocalizedPath(pageInfo.key, 'da', pageInfo.params);
    const enPath = getLocalizedPath(pageInfo.key, 'en', pageInfo.params);
    
    hreflangs.push({ lang: 'da', href: `${siteUrl}${daPath}` });
    hreflangs.push({ lang: 'en', href: `${siteUrl}${enPath}` });
    // Danish is the primary standard language, so x-default points to the Danish version
    hreflangs.push({ lang: 'x-default', href: `${siteUrl}${daPath}` });
  }

  return (
    <Helmet>
      {/* Primære Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <html lang={language} />

      {/* Hreflang Tags */}
      {hreflangs.map((item) => (
        <link key={item.lang} rel="alternate" hrefLang={item.lang} href={item.href} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Struktureret Data (JSON-LD) */}
      {schemaMarkup && (() => {
        const finalSchema = Array.isArray(schemaMarkup)
          ? combineSchemas(schemaMarkup)
          : (schemaMarkup['@context'] ? schemaMarkup : { '@context': 'https://schema.org', ...schemaMarkup });
        return finalSchema ? (
          <script type="application/ld+json">
            {JSON.stringify(finalSchema)}
          </script>
        ) : null;
      })()}
    </Helmet>
  );
};

export default SEO;
